import React, { useEffect, useState, useMemo } from "react";
import api from "./../../../const/axios";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { usePageParam } from "@/hooks/usePageParam";
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserStat {
  user_ID: number;
  name: string;
  surname: string;
  packageCount: number;
}

type SortKey = "name" | "packageCount";
type SortDir = "asc" | "desc";

// ─── Pagination ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

interface PaginationProps {
  current: number;
  total: number;
  onChange: (page: number) => void;
}

const SimplePagination: React.FC<PaginationProps> = ({ current, total, onChange }) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - current) <= 2)
    .reduce<(number | "ellipsis")[]>((acc, p, idx, arr) => {
      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("ellipsis");
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="flex items-center justify-end gap-2 mt-4">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {pages.map((p, i) =>
        p === "ellipsis" ? (
          <span key={`e-${i}`} className="w-9 text-center text-gray-400 dark:text-gray-500 text-sm">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p as number)}
            className={cn(
              "w-9 h-9 rounded-lg text-sm font-medium border transition-colors",
              current === p
                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-900 hover:text-blue-500 dark:hover:text-indigo-200"
            )}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === Math.ceil(total / PAGE_SIZE)}
        className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Sort icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ column: SortKey; sortKey: SortKey | null; sortDir: SortDir }> = ({ column, sortKey, sortDir }) => {
  if (sortKey !== column) return <ChevronsUpDown className="w-3.5 h-3.5 text-gray-300" />;
  return sortDir === "asc"
    ? <ChevronUp className="w-3.5 h-3.5 text-blue-500 dark:text-indigo-300" />
    : <ChevronDown className="w-3.5 h-3.5 text-blue-500 dark:text-indigo-300" />;
};

// ─── Main component ───────────────────────────────────────────────────────────

const UserStats: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<UserStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Page lives in the URL (?page=N) so it survives back/forward navigation
  const { page, setPage, goToPage } = usePageParam();

  useEffect(() => {
    api.get("/user-stats")
      .then(res => setData(res.data))
      .catch(err => console.error("Error fetching stats:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = searchText.toLowerCase();
    return data.filter(item =>
      `${item.name} ${item.surname}`.toLowerCase().includes(q)
    );
  }, [data, searchText]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") {
        cmp = `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`);
      } else {
        cmp = a.packageCount - b.packageCount;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const thCls = "px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide";
  const thSortCls = cn(thCls, "cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-100 transition-colors");

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{t("header.stats")} | 3sual.az</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">

        {/* Header card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-xl font-black text-gray-900 dark:text-gray-50">
            {t("stats.title")}
            {!loading && filtered.length > 0 && (
              <span className="ml-2 text-base font-semibold text-gray-400 dark:text-gray-500">({filtered.length})</span>
            )}
          </h1>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <Input
              className="pl-9"
              placeholder={t("stats.searchPlaceholder")}
              value={searchText}
              onChange={e => { setSearchText(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {/* Table card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
              <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">Yüklənir...</p>
            </div>
          ) : sorted.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-gray-400 dark:text-gray-500 font-medium">{t("search.noResults")}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/60">
                    <tr>
                      <th className={cn(thCls, "w-12")}>#</th>
                      <th
                        className={thSortCls}
                        onClick={() => handleSort("name")}
                      >
                        <span className="flex items-center gap-1">
                          İstifadəçi
                          <SortIcon column="name" sortKey={sortKey} sortDir={sortDir} />
                        </span>
                      </th>
                      <th
                        className={cn(thSortCls, "text-right")}
                        onClick={() => handleSort("packageCount")}
                      >
                        <span className="flex items-center justify-end gap-1">
                          Paket sayı
                          <SortIcon column="packageCount" sortKey={sortKey} sortDir={sortDir} />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {paginated.map((row, i) => (
                      <tr key={row.user_ID} className="hover:bg-gray-50/60 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 dark:text-gray-500 text-xs font-medium tabular-nums">
                          {(page - 1) * PAGE_SIZE + i + 1}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-900 dark:text-gray-50">
                            {row.name} {row.surname}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300">
                            {row.packageCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-4 border-t border-gray-50 dark:border-gray-800">
                <SimplePagination
                  current={page}
                  total={sorted.length}
                  onChange={goToPage}
                />
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default UserStats;
